import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  MapPinIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  IdentificationIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

// Add interfaces for field configuration
interface FieldConfiguration {
  is_enabled: boolean;
  is_required: boolean;
  placeholder_text: string;
  help_text: string;
  field_order: number;
}

interface PatientModelField {
  field_name: string;
  field_label: string;
  field_type: string;
  is_core: boolean;
  description: string;
  options?: string[];
}

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsappPhone?: string;
  gender: string;
  birthDate: string;
  address: string;
  profession?: string;
  cinNumber?: string;
  referredBy?: string;
  insurance?: string;
  medicalHistoryAllergies?: string;
  medicalHistoryDiseases?: string;
  medicalHistoryMedications?: string;
  familyHeadId?: string;
  relationshipToHead?: string;
  status: 'active' | 'inactive' | 'new';
  totalVisits: number;
  lastVisit: string;
  nextAppointment?: string;
}

interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPatient?: Patient | null;
  onSave: (patient: Partial<Patient>) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsappPhone: string;
  gender: string;
  birthDate: string;
  address: string;
  profession: string;
  cinNumber: string;
  referredBy: string;
  insurance: string;
  medicalHistoryAllergies: string;
  medicalHistoryDiseases: string;
  medicalHistoryMedications: string;
  familyHeadId: string;
  relationshipToHead: string;
}

// Field mapping between API field names and form field names
const fieldNameMapping: Record<string, keyof FormData> = {
  'first_name': 'firstName',
  'last_name': 'lastName',
  'email': 'email',
  'phone_number': 'phone',
  'whatsapp_phone': 'whatsappPhone',
  'gender': 'gender',
  'birth_date': 'birthDate',
  'address': 'address',
  'profession': 'profession',
  'cin_number': 'cinNumber',
  'referred_by': 'referredBy',
  'insurance': 'insurance',
  'medical_history_allergies': 'medicalHistoryAllergies',
  'medical_history_diseases': 'medicalHistoryDiseases',
  'medical_history_medications': 'medicalHistoryMedications',
  'family_head_id': 'familyHeadId',
  'relationship_to_head': 'relationshipToHead'
};

// Reverse mapping for sending data to API
const reverseFieldMapping: Record<keyof FormData, string> = {
  'firstName': 'first_name',
  'lastName': 'last_name',
  'email': 'email',
  'phone': 'phone_number',
  'whatsappPhone': 'whatsapp_phone',
  'gender': 'gender',
  'birthDate': 'birth_date',
  'address': 'address',
  'profession': 'profession',
  'cinNumber': 'cin_number',
  'referredBy': 'referred_by',
  'insurance': 'insurance',
  'medicalHistoryAllergies': 'medical_history_allergies',
  'medicalHistoryDiseases': 'medical_history_diseases',
  'medicalHistoryMedications': 'medical_history_medications',
  'familyHeadId': 'family_head_id',
  'relationshipToHead': 'relationship_to_head'
};

const AddPatientModal: React.FC<AddPatientModalProps> = ({
  isOpen,
  onClose,
  selectedPatient,
  onSave
}) => {
  const { t } = useTranslation(['patients', 'common']);
  
  const [formData, setFormData] = useState<FormData>({
    firstName: selectedPatient?.firstName || '',
    lastName: selectedPatient?.lastName || '',
    email: selectedPatient?.email || '',
    phone: selectedPatient?.phone || '',
    whatsappPhone: selectedPatient?.whatsappPhone || '',
    gender: selectedPatient?.gender || '',
    birthDate: selectedPatient?.birthDate || '',
    address: selectedPatient?.address || '',
    profession: selectedPatient?.profession || '',
    cinNumber: selectedPatient?.cinNumber || '',
    referredBy: selectedPatient?.referredBy || '',
    insurance: selectedPatient?.insurance || '',
    medicalHistoryAllergies: selectedPatient?.medicalHistoryAllergies || '',
    medicalHistoryDiseases: selectedPatient?.medicalHistoryDiseases || '',
    medicalHistoryMedications: selectedPatient?.medicalHistoryMedications || '',
    familyHeadId: selectedPatient?.familyHeadId || '',
    relationshipToHead: (selectedPatient as any)?.relationshipToHead || 'self'
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Add state for field configurations
  const [modelFields, setModelFields] = useState<PatientModelField[]>([]);
  const [fieldConfigs, setFieldConfigs] = useState<Record<string, FieldConfiguration>>({});
  const [configsLoading, setConfigsLoading] = useState(true);

  // Load field configurations
  useEffect(() => {
    const loadFieldConfigurations = async () => {
      try {
        setConfigsLoading(true);
        
        // Load model fields
        const modelResponse = await fetch('/api/patients/settings/model-fields/', {
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        });
        
        if (modelResponse.ok) {
          const modelData = await modelResponse.json();
          setModelFields(modelData.model_fields || []);
        }

        // Load field configurations
        const configResponse = await fetch('/api/patients/settings/field-config/', {
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        });
        
        if (configResponse.ok) {
          const configData = await configResponse.json();
          setFieldConfigs(configData.configured_fields || {});
        }
      } catch (error) {
        console.error('Failed to load field configurations:', error);
      } finally {
        setConfigsLoading(false);
      }
    };

    if (isOpen) {
      loadFieldConfigurations();
    }
  }, [isOpen]);

  // Update form data when selectedPatient changes
  useEffect(() => {
    if (selectedPatient) {
      setFormData({
        firstName: selectedPatient.firstName || '',
        lastName: selectedPatient.lastName || '',
        email: selectedPatient.email || '',
        phone: selectedPatient.phone || '',
        whatsappPhone: selectedPatient.whatsappPhone || '',
        gender: selectedPatient.gender || '',
        birthDate: selectedPatient.birthDate || '',
        address: selectedPatient.address || '',
        profession: selectedPatient.profession || '',
        cinNumber: selectedPatient.cinNumber || '',
        referredBy: selectedPatient.referredBy || '',
        insurance: selectedPatient.insurance || '',
        medicalHistoryAllergies: selectedPatient.medicalHistoryAllergies || '',
        medicalHistoryDiseases: selectedPatient.medicalHistoryDiseases || '',
        medicalHistoryMedications: selectedPatient.medicalHistoryMedications || '',
        familyHeadId: selectedPatient.familyHeadId || '',
        relationshipToHead: selectedPatient.relationshipToHead || 'self'
      });
    } else {
      // Reset form when no patient is selected (new patient)
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        whatsappPhone: '',
        gender: '',
        birthDate: '',
        address: '',
        profession: '',
        cinNumber: '',
        referredBy: '',
        insurance: '',
        medicalHistoryAllergies: '',
        medicalHistoryDiseases: '',
        medicalHistoryMedications: '',
        familyHeadId: '',
        relationshipToHead: 'self'
      });
    }
    // Clear any existing errors when patient changes
    setErrors({});
  }, [selectedPatient]);

  if (!isOpen) return null;

  // Helper function to get enabled fields
  const getEnabledFields = () => {
    return modelFields.filter(field => {
      const config = fieldConfigs[field.field_name];
      // Core fields are always enabled, non-core fields depend on configuration
      return field.is_core || (config && config.is_enabled);
    });
  };

  // Helper function to check if field is required
  const isFieldRequired = (fieldName: string) => {
    const field = modelFields.find(f => f.field_name === fieldName);
    const config = fieldConfigs[fieldName];
    // Core fields are always required, non-core fields depend on configuration
    return field?.is_core || (config && config.is_required);
  };

  // Helper function to get field label translation
  const getFieldLabel = (fieldName: string) => {
    const labelMapping: Record<string, string> = {
      'first_name': t('modal.fields.firstName'),
      'last_name': t('modal.fields.lastName'),
      'email': t('modal.fields.email'),
      'phone_number': t('modal.fields.phone'),
      'whatsapp_phone': t('modal.fields.whatsappPhone'),
      'gender': t('modal.fields.gender'),
      'birth_date': t('modal.fields.birthDate'),
      'address': t('modal.fields.address'),
      'profession': t('modal.fields.profession'),
      'cin_number': t('modal.fields.cinNumber'),
      'referred_by': t('modal.fields.referredBy'),
      'insurance': t('modal.fields.insurance'),
      'medical_history_allergies': t('modal.fields.medicalHistoryAllergies'),
      'medical_history_diseases': t('modal.fields.medicalHistoryDiseases'),
      'medical_history_medications': t('modal.fields.medicalHistoryMedications'),
      'family_head_id': t('modal.fields.familyHeadId'),
      'relationship_to_head': 'Lien de parenté'
    };
    return labelMapping[fieldName] || fieldName;
  };

  // Helper function to get field placeholder translation
  const getFieldPlaceholder = (fieldName: string) => {
    const placeholderMapping: Record<string, string> = {
      'first_name': t('modal.fields.firstNamePlaceholder'),
      'last_name': t('modal.fields.lastNamePlaceholder'),
      'email': t('modal.fields.emailPlaceholder'),
      'phone_number': t('modal.fields.phonePlaceholder'),
      'whatsapp_phone': t('modal.fields.whatsappPhonePlaceholder'),
      'gender': t('modal.fields.genderPlaceholder'),
      'birth_date': t('modal.fields.birthDatePlaceholder'),
      'address': t('modal.fields.addressPlaceholder'),
      'profession': t('modal.fields.professionPlaceholder'),
      'cin_number': t('modal.fields.cinNumberPlaceholder'),
      'referred_by': t('modal.fields.referredByPlaceholder'),
      'insurance': t('modal.fields.insurancePlaceholder'),
      'medical_history_allergies': t('modal.fields.medicalHistoryAllergiesPlaceholder'),
      'medical_history_diseases': t('modal.fields.medicalHistoryDiseasesPlaceholder'),
      'medical_history_medications': t('modal.fields.medicalHistoryMedicationsPlaceholder'),
      'family_head_id': t('modal.fields.familyHeadIdPlaceholder'),
      'relationship_to_head': 'Sélectionner le lien de parenté'
    };
    return placeholderMapping[fieldName] || '';
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    const enabledFields = getEnabledFields();

    enabledFields.forEach(field => {
      const apiFieldName = field.field_name;
      const formFieldName = fieldNameMapping[apiFieldName];
      
      if (!formFieldName) return;
      
      const value = formData[formFieldName];
      const required = isFieldRequired(apiFieldName);

      // Check required fields
      if (required && (!value || !value.toString().trim())) {
        newErrors[formFieldName] = t('modal.validation.required', { field: field.field_label });
        return;
      }

      // Type-specific validation
      if (value && value.toString().trim()) {
        switch (field.field_type) {
          case 'email':
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.toString())) {
              newErrors[formFieldName] = 'Please enter a valid email address';
            }
            break;
          case 'phone':
            if (!/^[\d\s\-\+\(\)]+$/.test(value.toString())) {
              newErrors[formFieldName] = 'Please enter a valid phone number';
            }
            break;
          case 'date':
            if (new Date(value.toString()) > new Date()) {
              newErrors[formFieldName] = 'Date cannot be in the future';
            }
            break;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      // Convert form data from camelCase to snake_case for API
      const apiFormData: any = {};
      Object.keys(formData).forEach(formFieldName => {
        const apiFieldName = reverseFieldMapping[formFieldName as keyof FormData];
        if (apiFieldName) {
          apiFormData[apiFieldName] = formData[formFieldName as keyof FormData];
        }
      });
      
      const patientData: Partial<Patient> = {
        // Use camelCase for the Patient interface (this might need to be converted back)
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        whatsappPhone: formData.whatsappPhone,
        gender: formData.gender,
        birthDate: formData.birthDate,
        address: formData.address,
        profession: formData.profession,
        cinNumber: formData.cinNumber,
        referredBy: formData.referredBy,
        insurance: formData.insurance,
        medicalHistoryAllergies: formData.medicalHistoryAllergies,
        medicalHistoryDiseases: formData.medicalHistoryDiseases,
        medicalHistoryMedications: formData.medicalHistoryMedications,
        familyHeadId: formData.familyHeadId,
        relationshipToHead: formData.relationshipToHead,
        status: selectedPatient ? selectedPatient.status : 'new' as const,
        totalVisits: selectedPatient ? selectedPatient.totalVisits : 0,
        lastVisit: selectedPatient ? selectedPatient.lastVisit : new Date().toISOString(),
        id: selectedPatient?.id || `patient-${Date.now()}`
      };

      console.log('Form data being sent:', formData);
      console.log('API formatted data:', apiFormData);
      console.log('Patient data:', patientData);

      onSave(patientData);
      onClose();
    } catch (error) {
      console.error('Error saving patient:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop Overlay */}
      <div 
        className="fixed bg-black/50 z-[70]" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          width: '100vw', 
          height: '100vh',
          zIndex: 70 
        }} 
        onClick={onClose} 
      />
      {/* Modal Container */}
      <div 
        className="fixed flex items-center justify-center z-[71] p-4 pointer-events-none" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          width: '100vw', 
          height: '100vh',
          zIndex: 71 
        }}
      >
        <div className="pointer-events-auto bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-card-foreground flex items-center">
              <div className="p-2 rounded-xl bg-primary/10 mr-3">
                <UserIcon className="h-6 w-6 text-primary" />
              </div>
              {selectedPatient ? t('modal.editTitle') : t('modal.addTitle')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {configsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">{t('common:loading')}</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Render fields dynamically based on configuration */}
              {getEnabledFields()
                .sort((a, b) => {
                  const configA = fieldConfigs[a.field_name];
                  const configB = fieldConfigs[b.field_name];
                  return (configA?.field_order || 0) - (configB?.field_order || 0);
                })
                .map((field) => {
                  const apiFieldName = field.field_name;
                  const formFieldName = fieldNameMapping[apiFieldName];
                  
                  // Skip fields that don't have a mapping
                  if (!formFieldName) {
                    console.warn(`No mapping found for field: ${apiFieldName}`);
                    return null;
                  }
                  
                  const fieldValue = formData[formFieldName] || '';
                  const isRequired = isFieldRequired(apiFieldName);
                  const error = errors[formFieldName];

                  const handleFieldChange = (value: string) => {
                    setFormData(prev => ({ ...prev, [formFieldName]: value }));
                  };

                  const getFieldIcon = () => {
                    switch (apiFieldName) {
                      case 'first_name':
                      case 'last_name':
                        return <UserIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'email':
                        return <EnvelopeIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'phone_number':
                      case 'whatsapp_phone':
                        return <PhoneIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'birth_date':
                        return <CalendarIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'address':
                        return <MapPinIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'profession':
                        return <BriefcaseIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'cin_number':
                        return <IdentificationIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'referred_by':
                        return <UserGroupIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'insurance':
                        return <ShieldCheckIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'medical_history_allergies':
                      case 'medical_history_diseases':
                      case 'medical_history_medications':
                        return <HeartIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'family_head_id':
                        return <UserGroupIcon className="h-4 w-4 text-muted-foreground" />;
                      case 'relationship_to_head':
                        return <UserGroupIcon className="h-4 w-4 text-muted-foreground" />;
                      default:
                        return <GlobeAltIcon className="h-4 w-4 text-muted-foreground" />;
                    }
                  };

                  const renderField = () => {
                    const placeholder = getFieldPlaceholder(apiFieldName);
                    
                    // Special handling for relationship_to_head field
                    if (apiFieldName === 'relationship_to_head') {
                      const relationshipOptions = [
                        { value: 'self', label: 'Assuré(e) - المؤمن(ة)' },
                        { value: 'spouse', label: 'Conjoint(e) - زوج(ة)' },
                        { value: 'child', label: 'Enfant - ابن/بنت' },
                        { value: 'parent', label: 'Parent - والد(ة)' },
                        { value: 'sibling', label: 'Frère/Sœur - أخ/أخت' },
                        { value: 'other', label: 'Autre - آخر' }
                      ];
                      return (
                        <select
                          value={fieldValue.toString()}
                          onChange={(e) => handleFieldChange(e.target.value)}
                          className={`w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-red-500' : ''}`}
                        >
                          <option value="">{placeholder}</option>
                          {relationshipOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      );
                    }

                    // Special handling for insurance field
                    if (apiFieldName === 'insurance') {
                      const insuranceOptions = ['CNOPS', 'CNSS', 'FAR', 'CMIM', 'other', 'none'];
                      return (
                        <select
                          value={fieldValue.toString()}
                          onChange={(e) => handleFieldChange(e.target.value)}
                          className={`w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-red-500' : ''}`}
                        >
                          <option value="">{placeholder}</option>
                          {insuranceOptions.map(option => (
                            <option key={option} value={option}>
                              {t(`modal.fields.insuranceOptions.${option}`, option)}
                            </option>
                          ))}
                        </select>
                      );
                    }
                    
                    switch (field.field_type) {
                      case 'select':
                        return (
                          <select
                            value={fieldValue.toString()}
                            onChange={(e) => handleFieldChange(e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-red-500' : ''}`}
                          >
                            <option value="">{placeholder || `${t('common:select')} ${getFieldLabel(field.field_name)}`}</option>
                            {field.options?.filter(option => {
                              // Remove "other" option from gender field
                              if (field.field_name === 'gender' && option.toLowerCase() === 'other') {
                                return false;
                              }
                              return true;
                            }).map(option => (
                              <option key={option} value={option}>
                                {option.charAt(0).toUpperCase() + option.slice(1)}
                              </option>
                            ))}
                          </select>
                        );
                      
                      case 'textarea':
                        return (
                          <textarea
                            value={fieldValue.toString()}
                            onChange={(e) => handleFieldChange(e.target.value)}
                            placeholder={placeholder}
                            className={`w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-h-[80px] resize-vertical ${error ? 'border-red-500' : ''}`}
                          />
                        );
                      
                      case 'date':
                        return (
                          <input
                            type="date"
                            value={fieldValue.toString()}
                            onChange={(e) => handleFieldChange(e.target.value)}
                            placeholder={placeholder}
                            className={`w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-red-500' : ''}`}
                          />
                        );
                      
                      default:
                        return (
                          <input
                            type={field.field_type === 'email' ? 'email' : 'text'}
                            value={fieldValue.toString()}
                            onChange={(e) => handleFieldChange(e.target.value)}
                            placeholder={placeholder}
                            className={`w-full pl-10 pr-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-red-500' : ''}`}
                          />
                        );
                    }
                  };

                  return (
                    <div key={field.field_name} className="form-group">
                      <label 
                        className="block text-sm font-medium mb-2 text-foreground"
                      >
                        {getFieldLabel(field.field_name)}
                        {isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          {getFieldIcon()}
                        </div>
                        {renderField()}
                      </div>
                      {error && (
                        <p className="mt-1 text-sm text-red-500">{error}</p>
                      )}
                    </div>
                  );
                })
                .filter(Boolean)} {/* Remove null values */}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>{t('common:saving')}</span>
                </div>
              ) : (
                selectedPatient ? t('modal.messages.updatePatient') : t('addPatient')
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default AddPatientModal;
